import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Row } from "@tanstack/react-table";
import { Addresses } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import gl from "assets/libs/moment/src/locale/gl";
import { getAuthCookie } from "../../utils/authCookies";

interface ExportRow {
  Name: string;
  Status: string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Addresses>();

const AgentAddresses = () => {
  const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");

  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const hasShownToast = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.successMessage && !hasShownToast.current) {
      toast.success(location.state.successMessage);
      hasShownToast.current = true;

      // Clear browser history state to avoid repeat toasts
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          // `${process.env.REACT_APP_API_URL}/office/${userId}/inventory/products`,
          `${process.env.REACT_APP_API_URL}/addresses/user/${userId}/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );
        const result = await response.json();
        setAddressObjects(result?.data ?? []);
        setRowCount(result?.total ?? 0);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token, globalFilter, pagination]);

  const fetchAllAddresses = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/addresses?all=true`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const result = await response.json();
    return result; // expected to be full dataset
  };

  useEffect(() => {
    const loadExportData = async () => {
      const allData = await fetchAllAddresses();
      const exportData: ExportRow[] = allData?.map((row: Addresses) => ({
        Name: row.label,
        City: row.address_details.city ? "Active" : "Inactive",
      }));
      setExportRows(exportData);
    };

    loadExportData();
  }, []);

  const handleDelete = async (addressId: string) => {
    setDeleting(addressId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the address.");
      }

      toast.success("Address deleted successfully!");
      // Update the state by removing the deleted license
      setAddressObjects((prev) =>
        prev?.filter((address) => address._id !== addressId)
      );
      setDeleting(null);
      // navigate(0);
    } catch (err) {
      toast.error("Failed to delete the address.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  const handleDeleteClick = (address: Addresses) => {
    setDeleteTarget({
      id: address._id,
      name: address.label || "Address",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.display({
        id: "label",
        header: () => <div className="th-text">Label of Address</div>,
        cell: (info) => {
          const address = info.row.original;
          return <div className="text-sm">{address.label}</div>;
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor("address_details.addressLine1", {
        header: () => <div className="th-text">Address line 1</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor("address_details.city", {
        header: () => <div className="th-text">City</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor("address_details.state", {
        header: () => <div className="th-text">State</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor("address_details.country", {
        header: () => <div className="th-text">Country</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const address = info.row.original;
          return (
            <div className="d-flex gap-2  table-cells">
              <EditIconButton
                to={`/agent/AgentAddEditAddresses/${address._id}`}
                state={{ type: "edit" }}
                className="edit me-2"
                title="Edit"
              />
              {/* <Link
                to={`/agent/editAddress/${address._id}`}
                className="border-0 p-0"
                title="Edit"
              >
                <i className="ri-pencil-fill fs-20"></i>
              </Link> */}
              <DeleteIconButton
                onClick={() => handleDeleteClick(address)}
                disabled={deleting === address._id}
                title="Delete"
              />
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, globalFilter]);


  const table = useReactTable({
    data: addressObjects,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content container">


      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          <Link to="/agent/agentAddEditAddresses" className="btn btn-primary  table-addbutton">
            + Add Address
          </Link>
        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0"> Addresses</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Label Name, City, Contact Person" value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
            {/* <ExportAsExcel
                          data={exportRows}
                          headers={[
                            "Company Name",
                            "First Name",
                            "Last Name",
                            "Phone",
                            "Email",
                            "License Status",
                            "Status",
                          ]}
                          fileName="BrokerageFirms"
                        >
                          {(props) => (
                            <button {...props} className="export-button">
                              <i className="ri-file-excel-2-line me-2" />
                              Export to Excel
                            </button>
                          )}
                        </ExportAsExcel> */}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      )}
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Address"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default AgentAddresses;
